import React, { PureComponent, createRef } from "react";
import "./PieChart.scss";
import { Question } from "../../global-interfaces";
import {
  select,
  pie,
  arc,
  format,
  range,
  interpolateNumber,
  interpolate,
  scaleLinear,
} from "d3";
import { checkIfMobile, checkBrowser } from "../../utils";
import { CUST_PERSONS_ORDER } from "../../constants";
import Classnames from "classnames";
import styleVars from "../../styling/variables.scss";
import { descending, ascending } from "d3-array";
const pct_format: any = format(".0%");

interface ChartProps {
  chart: Question;
  color: string | null;
}
interface State {
  width: number;
  hoveredSlice: number | null;
}

interface LabelProps {
  chartWidth: number;
  showLabel: boolean;
  flatLabel: boolean;
  centroid: Array<number>;
  value: string;
  label: string;
  radians: number;
}

class Label extends PureComponent<LabelProps> {
  render() {
    const { showLabel, flatLabel, centroid, label, value, radians } = this.props;
    const [x,y] = centroid
    const [tx, ty] = [x, y].map(d => d * 2.05)
    const section = Math.ceil((radians * 4) / Math.PI)
    return (
      <g className={Classnames(
        "label-group", 
        { show: showLabel })}
        transform={`translate(${tx}, ${ty})`}
      >
        <foreignObject x="0" y="0" width="1" height="1">
          <div className={
            Classnames("pie-label", `s${section}`, {
            flatLabel,
            nudgeUp: label === "Transgender or nonbinary",
            short: label.split("").length < 8,
            long: label.split("").length > 45
          } )}
            >
            <div className="pie-label-text">{label}</div>
            <div className="pie-label-value">{value}</div>
          </div>
        </foreignObject>  
      </g>
    );
  }
}

class PieChart extends PureComponent<ChartProps, State> {
  private chartDivRef: React.RefObject<HTMLDivElement>;
  private svgRef: React.RefObject<SVGSVGElement>;
  private size: number;

  constructor(props: ChartProps) {
    super(props);
    this.state = {
      width: 0,
      hoveredSlice: null,
    };
    this.svgRef = createRef();
    this.chartDivRef = createRef();
    this.onResize = this.onResize.bind(this);
    this.generatePie = this.generatePie.bind(this);
    this.size = +styleVars.chartHeight.slice(0, -2);
  }
  componentDidMount() {
    this.size =
      (this.chartDivRef.current && this.chartDivRef.current.offsetHeight) ||
      +styleVars.chartHeight.slice(0, -2);
    window.addEventListener("resize", () => {
      this.onResize();
    });
    this.onResize();
  }

  componentWillUnmount() {
    //@ts-ignore
    window.removeEventListener("resize", this.onResize()); // prevent memory leaks
  }

  componentDidUpdate() {
    const { chart } = this.props;
    const { data } = chart;
    const { width } = this.state;
    const radius = Math.min(width, this.size) / 2 - 40;
    const opacityScale = scaleLinear()
      //@ts-ignore
      .domain([data.length, 0])
      .range([0.2,1]);

    let arcGen = arc()
      .innerRadius(0)
      .outerRadius(radius)
      .cornerRadius(10);

    const pie = select(this.svgRef.current).select(".pie");
    let slice = pie.selectAll(".pie-path").data(this.generatePie(data));

    // @ts-ignore
    let sliceEnter = slice
      .enter()
      .append<SVGPathElement>("path")
      .attr("class", "pie-path")
      //@ts-ignore
      .attr("opacity", (d: any) => opacityScale(d.index))
      .on("mouseenter", (d: any) => {
        this.setState({ hoveredSlice: d.index });
      });

    // EXIT BEHAVIOR

    slice
      .exit()
      .transition()
      .duration(500)
      //@ts-ignore
      .attrTween("d", function(this: SVGPathElement, d: any) {
        let old_start_angle = d.startAngle;
        let old_end_angle = d.endAngle;
        // smallest to largest, clockwise. if the order is flipped, this must be 2 * Math.PI */
        let pie_top = 0; 
        let i = interpolateNumber(old_end_angle, pie_top);
        let j = interpolateNumber(old_start_angle, pie_top);
        return (
          function(t: number) {
            d.endAngle = i(t);
            d.startAngle = j(t);
            return arcGen(d) || null;
          } || null
        );
      })
      .remove();

    // UPDATE BEHAVIOR

    // @ts-ignore
    slice = sliceEnter
      //@ts-ignore
      .merge(slice)
      .transition()
      .duration(500)
      // @ts-ignore
      .attrTween("d", function(this: any, d: any) {
        this._current = this._current || d;
        const i = interpolate(this._current, d);
        this._current = i(0);
        return function(t: number) {
          return arcGen(i(t));
        };
      });

    // ENTER BEHAVIOR

    sliceEnter
      .transition()
      .duration(500)
      //@ts-ignore
      .attrTween("d", function(d: any) {
        let new_start_angle = d.startAngle;
        let new_end_angle = d.endAngle;
        // smallest to largest, clockwise. if the order is flipped, this must be 2 * Math.PI */
        let pie_top = 0; 
        let i = interpolateNumber(pie_top, new_start_angle);
        let j = interpolateNumber(pie_top, new_end_angle);
        return function(t: number) {
          d.endAngle = i(t);
          d.startAngle = j(t);
          return arcGen(d);
        };
      });
  }

  onResize() {
    const width =
      (this.chartDivRef.current &&
        this.chartDivRef.current.getBoundingClientRect().width) ||
      0;
    this.setState({
      width,
    });
  }

  generatePie(data: Question["data"]) {
    return pie()
      .sort((a: any, b: any) => ascending(a.data_id, b.data_id))
      //@ts-ignore
      .value((d: any) => d.value)(data)
  }

  render() {
    const { chart, color } = this.props;
    const { data } = chart;
    const { width, hoveredSlice } = this.state;
    const radius = Math.min(width, this.size) / 2 - 40;
    const slices = this.generatePie(data);
    const extendData = (slice: any) => ({
      ...slice,
      innerRadius: 0,
      outerRadius: radius,
    });
    const twistData = (slice: any) => ({
      ...slice,
      startAngle: slice.startAngle - Math.PI / 5,
      endAngle: slice.endAngle - Math.PI / 5,
      innerRadius: 0,
      outerRadius: radius,
    });
    const arcGen = arc()
      .innerRadius(0)
      .outerRadius(radius);
      
    return (
      <div className={Classnames("PieChart", color)} ref={this.chartDivRef}>
        <svg id={"svg"} ref={this.svgRef} height={this.size} width={this.size}>
          <g
            className="pie"
            transform={`translate(${width / 2}, ${this.size / 2})`}
          />
          <g
            className="labels"
            transform={`translate(${width / 2}, ${this.size / 2})`}
          >
            {slices.map((d: any, i: number) => (
              <Label
                key={i}
                chartWidth={width}
                showLabel={
                  // percent cut off for showing or hiding labels
                  d.value > (checkIfMobile() ? 0.06 : 0.03) ||
                  hoveredSlice === d.index
                }
                flatLabel={d.value < 0.07 && d.index === 0} // a top, small label should extent without wrap
                centroid={slices.length === 2 && slices[0].value < .7 && slices[0].value > .5
                  /* given the common 60/ 40 pie charts, labels were often very close to the left and right edges of the chart. this allows the labels to twist slightly into opposite corners, and helps with spacing. */
                  ? arcGen.centroid(twistData(d))
                  : arcGen.centroid(extendData(d))}
                value={pct_format(d.value)}
                label={d.data.label}
                radians={((d.endAngle - d.startAngle) / 2) + d.startAngle}
              />
            ))}
          </g>
        </svg>
      </div>
    );
  }
}

export default PieChart;
